"use client";

import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Motion engine for the homepage — ported from the prototype's index.js
 * (the GSAP section). Targets DOM nodes rendered by HomePage by class name.
 *
 * Key safety property carried over from the prototype: every entrance uses
 * `gsap.from(...)`, so if JS is disabled or GSAP fails to load, the content is
 * simply already visible (no invisible-text bug). prefers-reduced-motion users
 * get the page with no animation at all.
 */
export function HomeMotion() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // No animation. The count-up numbers are rendered with their final value SSR,
      // but be defensive.
      document.querySelectorAll<HTMLElement>(".counter").forEach((el) => {
        const to = Number(el.dataset.to || "0");
        el.textContent = (el.dataset.prefix || "") + to.toLocaleString("pt-BR") + (el.dataset.suffix || "");
      });
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Hero com slider: entrada da coluna de texto e do palco (`.from` = visível sem JS).
      // O slider em si anima via CSS (HeroSlider + app.css), não via GSAP.
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" }, delay: 0.1 });
      heroTl
        .from(".hx__kicker", { opacity: 0, y: 10, duration: 0.6 })
        .from(".hx__title", { opacity: 0, y: 24, duration: 0.8 }, "-=0.3")
        .from(".hx__slides", { opacity: 0, y: 16, duration: 0.6 }, "-=0.45")
        .from(".hx__visual", { opacity: 0, scale: 0.96, duration: 0.9 }, "-=0.6")
        .from(".hx__lead", { opacity: 0, y: 20, duration: 0.6 }, "-=0.4");

      // Parallax
      gsap.to(".hx__glow--a", {
        y: 120,
        scrollTrigger: { trigger: ".hx", start: "top top", end: "bottom top", scrub: 1 },
      });
      gsap.to(".cosmos__nebula--a", { y: 200, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
      gsap.to(".cosmos__nebula--b", { y: -260, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
      gsap.to(".cosmos__nebula--c", { y: 180, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });

      // Reveals
      gsap.utils.toArray<HTMLElement>(".reveal").forEach((el) => {
        gsap.from(el, { opacity: 0, y: 40, duration: 0.9, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" } });
      });
      gsap.utils.toArray<HTMLElement>(".reveal-tilt").forEach((el) => {
        gsap.from(el, { opacity: 0, y: 60, rotateX: -12, duration: 1, ease: "power3.out", transformOrigin: "center top", scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" } });
      });
      gsap.utils.toArray<HTMLElement>(".reveal-fade").forEach((el) => {
        gsap.from(el, { opacity: 0, duration: 1.2, ease: "power2.out", scrollTrigger: { trigger: el, start: "top 92%", toggleActions: "play none none none" } });
      });

      // Stat counters
      gsap.utils.toArray<HTMLElement>(".counter").forEach((el) => {
        const end = parseFloat(el.dataset.to || "0");
        const prefix = el.dataset.prefix || "";
        const suffix = el.dataset.suffix || "";
        const obj = { v: 0 };
        ScrollTrigger.create({
          trigger: el,
          start: "top 85%",
          once: true,
          onEnter: () => {
            el.textContent = prefix + "0" + suffix; // start the count-up from zero
            gsap.to(obj, {
              v: end,
              duration: 1.8,
              ease: "power3.out",
              onUpdate: () => {
                el.textContent = prefix + Math.round(obj.v).toLocaleString("pt-BR") + suffix;
              },
            });
          },
        });
      });

      // Pillar 3D tilt
      document.querySelectorAll<HTMLElement>(".pillar").forEach((card) => {
        const onMove = (e: MouseEvent) => {
          const r = card.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          card.style.setProperty("--mx", x * 100 + "%");
          card.style.setProperty("--my", y * 100 + "%");
          gsap.to(card, { rotateX: (0.5 - y) * 8, rotateY: (x - 0.5) * 10, duration: 0.4, ease: "power2.out" });
        };
        const onLeave = () => gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.6, ease: "power3.out" });
        card.addEventListener("mousemove", onMove);
        card.addEventListener("mouseleave", onLeave);
      });

      // Magnetic CTAs
      document.querySelectorAll<HTMLElement>(".btn-magnetic").forEach((btn) => {
        const onMove = (e: MouseEvent) => {
          const r = btn.getBoundingClientRect();
          const x = e.clientX - r.left - r.width / 2;
          const y = e.clientY - r.top - r.height / 2;
          gsap.to(btn, { x: x * 0.18, y: y * 0.3, duration: 0.4, ease: "power2.out" });
        };
        const onLeave = () => gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.4)" });
        btn.addEventListener("mousemove", onMove);
        btn.addEventListener("mouseleave", onLeave);
      });

      // Scroll progress
      const progress = document.querySelector<HTMLElement>(".scroll-progress");
      if (progress) {
        ScrollTrigger.create({
          start: 0,
          end: "max",
          onUpdate: (self) => {
            progress.style.width = self.progress * 100 + "%";
          },
        });
      }

      // Hue shift
      gsap.to(".cosmos", {
        filter: "hue-rotate(40deg)",
        scrollTrigger: { trigger: "#why-section", start: "top center", end: "bottom center", scrub: true },
      });

      // The page renders content visible by default; once we're set up the
      // initial states above hide the few intro-only elements. Refresh ST after
      // fonts/layout settle.
      requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    return () => ctx.revert();
  }, []);

  return null;
}
